import React from 'react';
import { Check, Clock, Smile, Sparkles, Zap, Feather } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLang } from '@/hooks/useLang';
import { buildLangPath, stripLangPrefix } from '@/utils/routeBuilders';

const BenefitsSection = () => {
  const { t } = useTranslation('bonding');
  const navigate = useNavigate();
  const { lang } = useLang();

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();

    const scrollToContact = () => {
      const contactForm = document.getElementById('contact-form');
      if (contactForm) {
        const yOffset = -100; // Offset to account for fixed header
        const y =
          contactForm.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    };

    if (stripLangPrefix(window.location.pathname) !== '/') {
      navigate(buildLangPath(lang, '/'));
      setTimeout(scrollToContact, 100);
    } else {
      scrollToContact();
    }
  };

  const benefits = [
    {
      icon: <Clock className="h-12 w-12 text-dental" />,
      title: t('benefits.items.0.title'),
      description: t('benefits.items.0.description'),
    },
    {
      icon: <Feather className="h-12 w-12 text-dental-coral" />,
      title: t('benefits.items.1.title'),
      description: t('benefits.items.1.description'),
    },
    {
      icon: <Smile className="h-12 w-12 text-dental-mint" />,
      title: t('benefits.items.2.title'),
      description: t('benefits.items.2.description'),
    },
    {
      icon: <Sparkles className="h-12 w-12 text-dental-purple" />,
      title: t('benefits.items.3.title'),
      description: t('benefits.items.3.description'),
    },
    {
      icon: <Zap className="h-12 w-12 text-dental-gold" />,
      title: t('benefits.items.4.title'),
      description: t('benefits.items.4.description'),
    },
    {
      icon: <Check className="h-12 w-12 text-dental-tertiary" />,
      title: t('benefits.items.5.title'),
      description: t('benefits.items.5.description'),
    }
  ];

  return (
    <section className="py-16 lg:py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-20 right-0 w-80 h-80 bg-dental-light/20 rounded-full blur-3xl -z-10"></div>
      <div className="absolute bottom-20 left-0 w-60 h-60 bg-dental-coral/10 rounded-full blur-3xl -z-10"></div>
      
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 text-gradient-primary inline-block relative reveal" data-direction="up">
            {t('benefits.heading')}
          </h2>
          <p className="text-xl text-dental-dark/80 max-w-3xl mx-auto reveal" data-direction="up">
            {t('benefits.intro')}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div 
              key={index} 
              className="benefit-card group reveal" 
              data-direction="up" 
              data-delay={0.1 * index}
            >
              <div className="mb-6">
                {benefit.icon}
              </div>
              <h3 className="text-xl font-display font-bold mb-3 text-dental-dark group-hover:text-dental transition-colors duration-300">
                {benefit.title}
              </h3>
              <p className="text-dental-dark/70">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
        
        <div className="mt-16 text-center">
          <div className="inline-block px-6 py-3 bg-dental-light/30 rounded-full text-dental-dark/80 font-medium text-lg reveal" data-direction="up">
            {t('benefits.bottomLinePrefix')}{" "}
            <a
              href={buildLangPath(lang, '/')}
              onClick={handleContactClick}
              className="text-dental font-semibold hover:underline"
            >
              {t('benefits.bottomLineCta')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
